import React, { useCallback, useMemo, useRef } from 'react';
import type { RefObject } from 'react';

import { ErrorScreen } from './ErrorScreen';
import { LoginPage } from './LoginPage';
import { StorageAccessPage } from './StorageAccessPage';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { type UrlProps, useUrl } from './useUrl';

import { useValidationState, Subjects, LoadingStatus } from './useValidationState';
import { type NavigationPolicy } from '../navigation-policy';
import { ANALYTICS_CHANNEL } from '../analytics';

export type ChildrenProps = {
	handleContentLoadStarted: () => void;
	handleIframeOnLoad: () => void;
	handleIframeUnload: () => void;
	iframeLoaded?: boolean;
	iframeRef: RefObject<HTMLIFrameElement>;
	isLoading?: boolean;
	navigationPolicy?: NavigationPolicy;
	redirectedToLogin?: boolean;
};

export type Props = UrlProps & {
	children: (props: ChildrenProps) => React.ReactElement;
	navigationPolicy?: NavigationPolicy;
};

type CrossDomainErrorValidationProps = Pick<
	Props,
	'allowedFeatures' | 'children' | 'contentId' | 'navigationPolicy' | 'parentProduct' | 'spaceKey'
> & {
	embeddedConfluenceUrl: ReturnType<typeof useUrl>['embeddedConfluenceUrl'];
	standaloneConfluenceUrl: ReturnType<typeof useUrl>['standaloneConfluenceUrl'];
};

const noop = () => {};

export const PageLoadValidation = (
	props: Props,
): string | number | boolean | Iterable<React.ReactNode> | React.JSX.Element | null | undefined => {
	const { standaloneConfluenceUrl, embeddedConfluenceUrl } = useUrl(props);
	const {
		state: { contentLoadStarted, iframeLoaded },
	} = useValidationState();

	const isEmbeddedUrlOnSameDomain = embeddedConfluenceUrl
		? window.location.origin === new URL(embeddedConfluenceUrl).origin
		: false;

	const unusedIframeRef = useRef<HTMLIFrameElement>(null);

	return isEmbeddedUrlOnSameDomain ? (
		props.children({
			isLoading: (contentLoadStarted && !iframeLoaded) || false, // if same domain, we bypass loading screen in favor of internal iframe loading experience
			iframeLoaded: iframeLoaded || true, // if same domain, set to true so iframe can handle loading experience
			iframeRef: unusedIframeRef,
			handleIframeOnLoad: noop,
			handleContentLoadStarted: noop,
			handleIframeUnload: noop,
			navigationPolicy: props.navigationPolicy,
		})
	) : (
		<CrossDomainErrorValidation
			key={embeddedConfluenceUrl || props.contentId}
			standaloneConfluenceUrl={standaloneConfluenceUrl}
			embeddedConfluenceUrl={embeddedConfluenceUrl}
			allowedFeatures={props.allowedFeatures}
			{...props}
		/>
	);
};

const CrossDomainErrorValidation = ({
	allowedFeatures,
	children,
	contentId,
	embeddedConfluenceUrl,
	navigationPolicy,
	parentProduct,
	spaceKey,
	standaloneConfluenceUrl,
}: CrossDomainErrorValidationProps) => {
	const { state, dispatch } = useValidationState();

	const {
		iframeLoaded,
		contentLoadStarted,
		shouldAskStorageAccess,
		shouldAskToLogin,
		redirectedToLogin,
		iframeUnloaded,
	} = state;

	const iframeRef = useRef<HTMLIFrameElement>(null);

	/* This ref is specifically used for the purpose of recoding whether or not a delayed dispatch of the iframe occured.
		This recorded value is later used to fire analytics if contentLoadStarted arrives after the delayed dispatch, and reset
		once the event has fired or if the users needs to go through login flow.
	*/
	const iframeLoadedWithDelayRef = useRef(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isLoginFlowDisabled = useMemo(
		() => allowedFeatures?.includes('disable-login-flow') ?? false,
		[allowedFeatures],
	);

	const reloadEP = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.RELOAD_EP,
		});
	}, [dispatch]);

	const failToLoad = useMemo(
		() =>
			(iframeLoaded && !contentLoadStarted) ||
			/*
      If anonymous access is permitted globally but restricted on a content's parent space,
      the iframe will attempt a login redirect which we intercept.
      To avoid an error in the iframe, we need to wait for the iframe window to unload
      before unmouting the Iframe component.
    */
			(iframeUnloaded && redirectedToLogin),
		[contentLoadStarted, iframeLoaded, iframeUnloaded, redirectedToLogin],
	);

	const pageNavigationPolicy = useMemo(
		(): NavigationPolicy => ({
			navigate(url, modifiers, defaultNavigate) {
				// modifiy the navigationPolicy to intercept login redirects
				// so that login can be handled outside of the iframe
				if (url.includes('/login?application=confluence')) {
					dispatch({
						subject: Subjects.CONTENT_LOAD,
						status: LoadingStatus.REDIRECTED_TO_LOGIN,
					});
					return;
				}
				return navigationPolicy?.navigate
					? navigationPolicy.navigate(url, modifiers, defaultNavigate)
					: defaultNavigate(url, modifiers);
			},
			shimUrl: navigationPolicy?.shimUrl,
			domainNamesToReplace: navigationPolicy?.domainNamesToReplace,
			spaceKeyFilter: navigationPolicy?.spaceKeyFilter,
		}),
		[navigationPolicy, dispatch],
	);

	const handleIframeOnLoad = useCallback(() => {
		const dispatchNow = () => {
			dispatch({
				subject: Subjects.CONTENT_LOAD,
				status: LoadingStatus.IFRAME_LOADED,
			});
		};

		const totalNumberOfTries = 2;
		const iframeOfDispatch = iframeRef.current;
		const iframeSrcOfDispatch = iframeOfDispatch?.src;
		let tries = 0;
		const dispatchWithDelay = () => {
			/* We verify whether the iframeRef is current, as our intent is to invoke this only for the Iframe component created within the Content component (embedded-confluence-common/src/content/Content.tsx).
			It's important to note that the same Iframe component is also used by the StorageAccessPage component; however, we do not need to perform that check in this context.
			Additionally, the src of the iframe may change during transitions between view/edit modes or other redirects. Therefore, we must ensure that our invocation is limited to the current src of the iframe.
			*/
			if (iframeRef.current !== iframeOfDispatch || iframeOfDispatch?.src !== iframeSrcOfDispatch) {
				return;
			}
			++tries;
			if (contentLoadStarted || tries > totalNumberOfTries) {
				// If content load arrived after a timeout, failure to load was prevented by the delay
				if (contentLoadStarted && iframeLoadedWithDelayRef.current) {
					createAnalyticsEvent({
						action: 'delayedDispatch',
						actionSubject: 'iframeOnLoad',
						actionSubjectId: 'crossDomainErrorValidation',
						source: 'pageLoadValidation',
						eventType: 'track',
					}).fire(ANALYTICS_CHANNEL);

					//reset the ref for now. If the delayed dispatch is triggered again, the event will get fired too
					iframeLoadedWithDelayRef.current = false;
				}
				dispatchNow();
			} else {
				/* This determines whether to fire a track analytics event
				to log instances where iframe load is delayed to wait for the confluence content to load.*/
				if (!iframeLoadedWithDelayRef.current) {
					iframeLoadedWithDelayRef.current = true;
				}
				setTimeout(dispatchWithDelay, 1000);
			}
		};
		dispatchWithDelay();
	}, [contentLoadStarted, createAnalyticsEvent, dispatch]);

	const handleContentLoadStarted = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.CONTENT_LOAD_STARTED,
		});
	}, [dispatch]);

	const handleIframeUnload = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.IFRAME_UNLOADED,
		});
	}, [dispatch]);

	if (!failToLoad) {
		return children({
			iframeRef,
			isLoading: contentLoadStarted && !iframeLoaded,
			iframeLoaded,
			handleIframeOnLoad,
			handleContentLoadStarted,
			handleIframeUnload,
			navigationPolicy: pageNavigationPolicy,
			redirectedToLogin,
		});
	}

	if (!isLoginFlowDisabled) {
		if (shouldAskStorageAccess) {
			return (
				<StorageAccessPage
					url={embeddedConfluenceUrl}
					contentId={contentId}
					spaceKey={spaceKey}
					parentProduct={parentProduct}
				/>
			);
		}
		if (shouldAskToLogin) {
			return (
				<LoginPage
					contentId={contentId}
					parentProduct={parentProduct}
					reloadEP={reloadEP}
					spaceKey={spaceKey}
					url={embeddedConfluenceUrl}
				/>
			);
		}
	}
	return <ErrorScreen standaloneConfluenceUrl={standaloneConfluenceUrl} />;
};
